/**
 * Wire types shared by every consumer of the auth Worker's `/history/*`
 * routes. These mirror the payload shapes in
 * `workers/auth/src/handlers/history.ts` / `historyStats.ts` — that handler
 * code is the authority.
 */

/**
 * WebSocket namespace slugs, as registered in `server/src/index.ts`.
 * Intentionally duplicated from `@bn-games/social-client`'s identical
 * definition rather than imported — per the server-history-sync plan doc,
 * this union is too small to be worth a cross-package dependency.
 */
export const ONLINE_GAMES = ['backgammon', 'checkers', 'domino', 'mahjong', 'chess'] as const;

export type OnlineGame = (typeof ONLINE_GAMES)[number];

export function isOnlineGame(value: unknown): value is OnlineGame {
  return typeof value === 'string' && (ONLINE_GAMES as readonly string[]).includes(value);
}

export const MATCH_OUTCOMES = ['win', 'loss', 'draw', 'abandoned'] as const;

export type MatchOutcome = (typeof MATCH_OUTCOMES)[number];

export function isMatchOutcome(value: unknown): value is MatchOutcome {
  return typeof value === 'string' && (MATCH_OUTCOMES as readonly string[]).includes(value);
}

/**
 * A full match record as returned by `GET /history/<game>`. `players` /
 * `seed` / `moves` stay `unknown` — this package is generic over every
 * game's payload shape exactly like the Worker is; the per-app caller casts
 * them to its own `GameRecord`-derived shape.
 */
export interface MatchRecord {
  id: string;
  game: OnlineGame;
  variant: string;
  outcome: MatchOutcome;
  players: unknown;
  seed: unknown;
  moves: unknown;
  createdAt: number;
  finishedAt: number;
}

/**
 * The body `recordMatch` posts to `POST /history/<game>` — same fields as
 * `MatchRecord` minus `game`, which is a URL path segment (the caller's own
 * first argument to `recordMatch`), not a body field.
 */
export interface RecordMatchInput {
  id: string;
  variant: string;
  outcome: MatchOutcome;
  players: unknown;
  seed: unknown;
  moves: unknown;
  createdAt: number;
  finishedAt: number;
}

export interface GameStats {
  wins: number;
  losses: number;
  draws: number;
  abandoned: number;
  gamesPlayed: number;
}

export interface HistoryStats {
  byGame: Record<OnlineGame, GameStats>;
  overall: GameStats;
}
